import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, patch, post } from "@/lib/api";
import { isValidPostcodeOrArea, normalizePostcode } from "@/lib/constants";
import { fetchLocksmiths } from "@/lib/queries";
import { FieldError } from "./States";
import { AddressRoleFields } from "./AddressRoleFields";
import { cleanMeterDetails, hasIncompleteMeterDetails, normalizeMeterDetails } from "./MeterDetailsFields";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const blank = { postcode: "", full_address: "", meter_details: [{ type: "", serial: "" }], instructions: "", notes: "", agent_notes: "", engineer_notes: "", dog_handler_notes: "", dog_handler_id: "", customer_name: "", customer_telephone: "", debt_amount: "", vulnerability_text: "", previous_visits: "", engineer_type: "", engineer_id: "", has_shutter: false, shutter_details: "" };

export const AddAddressDialog = ({ open, onOpenChange, runId, onAdded, address = null, warrantTypes = [], runEngineerId = "", jobTypes }) => {
  const { profile } = useAuth();
  const isManager = profile?.role === "manager";
  const [f, setF] = useState(blank);
  const [engineers, setEngineers] = useState([]);
  const [dogHandlers, setDogHandlers] = useState([]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setF(address ? { ...Object.fromEntries(Object.keys(blank).map((k) => [k, address[k] ?? blank[k]])), meter_details: normalizeMeterDetails(address.meter_details) } : blank);
      setErrors({});
    }
  }, [open, address]);

  useEffect(() => {
    if (!open || !isManager || ((!warrantTypes.includes("engineer") || runEngineerId) && !warrantTypes.includes("dog_handler"))) return;
    fetchLocksmiths().then((members) => {
      setEngineers(members.filter((member) => (member.user_types || [member.role]).includes("engineer") && member.membership_status === "active"));
      setDogHandlers(members.filter((member) => (member.user_types || [member.role]).includes("dog_handler") && member.membership_status === "active"));
    }).catch(() => { setEngineers([]); setDogHandlers([]); });
  }, [open, isManager, warrantTypes, runEngineerId]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!isValidPostcodeOrArea(f.postcode)) errs.postcode = "Enter a valid UK postcode or the first part (e.g. E13)";
    if (warrantTypes.includes("engineer") && hasIncompleteMeterDetails(f.meter_details)) errs.meter_details = "Each meter needs a type and serial";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const body = {
        postcode: normalizePostcode(f.postcode), full_address: f.full_address.trim(), meter_details: cleanMeterDetails(f.meter_details), notes: f.notes.trim() || null,
        agent_notes: f.agent_notes.trim() || null, engineer_notes: f.engineer_notes.trim() || null, dog_handler_notes: f.dog_handler_notes.trim() || null,
        ...(isManager ? { engineer_id: !runEngineerId && f.engineer_id ? f.engineer_id : null } : {}),
        ...(isManager ? { dog_handler_id: warrantTypes.includes("dog_handler") ? f.dog_handler_id || null : null } : {}),
        customer_name: f.customer_name.trim() || null, customer_telephone: f.customer_telephone.trim() || null,
        debt_amount: f.debt_amount.trim() || null, vulnerability_text: f.vulnerability_text.trim() || null,
        previous_visits: f.previous_visits === "" ? null : Number(f.previous_visits), engineer_type: f.engineer_type || null,
        has_shutter: f.has_shutter, shutter_details: f.has_shutter ? f.shutter_details.trim() || null : null,
      };
      if (isManager) {
        body.instructions = f.instructions.trim() || null;
      }
      const saved = address ? await patch(`/addresses/${address.id}`, body) : await post(`/runs/${runId}/addresses`, body);
      toast.success(address ? "Address updated" : "Address added");
      onAdded(saved);
      onOpenChange(false);
    } catch (err) {
      setErrors({ form: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="add-address-dialog">
        <DialogHeader><DialogTitle className="font-display">{address ? "Edit address" : "Add address"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <Label htmlFor="aa-pc">Postcode *</Label>
            <Input id="aa-pc" value={f.postcode} onChange={(e) => setF({ ...f, postcode: e.target.value })} className="font-data mt-1.5 h-11 uppercase" data-testid="add-address-postcode-input" />
            <FieldError message={errors.postcode} testId="add-address-postcode-error" />
          </div>
          <div>
            <Label htmlFor="aa-fa">Full address <span className="font-normal text-slate-500">(optional)</span></Label>
            <Textarea id="aa-fa" rows={2} value={f.full_address} onChange={(e) => setF({ ...f, full_address: e.target.value })} className="mt-1.5" data-testid="add-address-full-address-input" />
            <FieldError message={errors.full_address} testId="add-address-full-address-error" />
          </div>
          <AddressRoleFields value={f} onChange={(p) => setF({ ...f, ...p })} warrantTypes={warrantTypes} testId="add-address" engineers={engineers} dogHandlers={dogHandlers} runEngineerId={runEngineerId} allowEngineerAssignment={isManager} jobTypes={jobTypes} meterDetailsError={errors.meter_details} />
          {isManager && (
          <div>
            <Label htmlFor="aa-in">Manager notes</Label>
            <Textarea id="aa-in" rows={2} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} className="mt-1.5" data-testid="add-address-instructions-input" />
          </div>
          )}
          {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="add-address-form-error">{errors.form}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="add-address-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} className="h-11 bg-blue-700 hover:bg-blue-800" data-testid="add-address-save-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{address ? "Save address" : "Add address"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
